import SEO from "@/components/SEO";
import { useState, useEffect, useRef } from "react";
import { webPageSchema, breadcrumbSchema } from "@/lib/organization-schema";
import { z } from "zod";
import { Link } from "react-router-dom";
import { CheckCircle, Phone, Mail, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { NativeSelect } from "@/components/ui/native-select";

const TURNSTILE_SITE_KEY = "0x4AAAAAADFnaRJpFiGWqOrF";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const PHONE_DISPLAY = "1-833-707-HOPE";
const PHONE_TEL = "tel:8337074673";
const EMAIL = "hello@abbashouse.org";

const GIVE_URL = "https://give.abbashouse.org";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be under 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be under 255 characters"),
  program: z.string().min(1, "Please select a program"),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be under 2000 characters"),
});

type FormData = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

const programs = [
  "90-Day Transition Program",
  "Direct Entry Program",
  "Abba's Second Chance",
  "General Inquiry",
  "Volunteer / Partnership",
];

export default function Contact() {
  const [form, setForm] = useState<FormData>({ name: "", email: "", program: "", message: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (submitted) return;
    const SCRIPT_ID = "cf-turnstile-script";
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(null),
        "expired-callback": () => setTurnstileToken(null),
        theme: "light",
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          renderWidget();
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [submitted]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof FormData;
        if (!fieldErrors[field]) fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (!turnstileToken) {
      setSubmitError("Please complete the verification challenge before sending.");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: { name: form.name, email: form.email, program: form.program, message: form.message, turnstileToken },
      });
      if (error) throw error;

      // Forward to Abba's Portal ticket system (client-side fallback)
      try {
        await fetch("https://uclrptgeoocihaufgdou.supabase.co/functions/v1/contact-form-webhook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            subject: form.program || "Contact Form Submission",
            message: form.message,
          }),
        });
      } catch (portalErr) {
        console.warn("Portal webhook failed:", portalErr);
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError("Something went wrong sending your message. Please try again or email us directly.");
      setTurnstileToken(null);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main>
      <SEO
        title="Contact & Apply"
        description="Contact Abba's House Communities or apply for one of our housing programs. Reach out about the 90-Day Transition Program, Direct Entry, Independent Membership, or Abba's Second Chance."
        canonical="https://abbashouse.org/contact"
        jsonLd={[
          webPageSchema({ name: "Contact & Apply — Abba's House Communities", description: "Contact Abba's House Communities or apply for one of our housing programs.", url: "https://abbashouse.org/contact" }),
          breadcrumbSchema([
            { name: "Home", url: "https://abbashouse.org/" },
            { name: "Contact & Apply", url: "https://abbashouse.org/contact" },
          ]),
        ]}
      />
      <section className="bg-primary text-primary-foreground py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-6">Reach Out</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-none mb-8 max-w-3xl">
            Contact Us or Apply.
          </h1>
          <p className="text-lg sm:text-xl opacity-70 leading-relaxed max-w-2xl mb-10">
            Whether you're seeking help, looking to volunteer, or want to partner with us — we'd love to hear from you. Fill out the form below and our team will be in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href={PHONE_TEL}
              className="inline-flex items-center gap-3 text-base font-semibold opacity-80 hover:opacity-100 transition-opacity"
            >
              <Phone className="h-5 w-5" />
              {PHONE_DISPLAY}
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="inline-flex items-center gap-3 text-base font-semibold opacity-80 hover:opacity-100 transition-opacity"
            >
              <Mail className="h-5 w-5" />
              {EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          {submitted ? (
            <div className="text-center py-16 flex flex-col items-center gap-6">
              <CheckCircle className="w-16 h-16 text-primary" strokeWidth={1.5} />
              <h2 className="text-3xl font-black tracking-tight">Message Received</h2>
              <p className="text-muted-foreground text-lg max-w-md">
                Thank you for reaching out. A member of our team will be in touch with you shortly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: "", email: "", program: "", message: "" }); }}
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-foreground text-foreground text-base font-semibold tracking-wide hover:bg-foreground hover:text-background transition-colors"
                >
                  Send Another Message
                </button>
                <Link
                  to="/programs"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity"
                >
                  View Our Programs
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-bold tracking-wide">
                    Full Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="Your full name"
                    className={`w-full rounded-xl border px-4 py-3 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition ${errors.name ? "border-destructive focus:ring-destructive" : "border-border"}`}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-bold tracking-wide">
                    Email Address <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    maxLength={255}
                    placeholder="you@example.com"
                    className={`w-full rounded-xl border px-4 py-3 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition ${errors.email ? "border-destructive focus:ring-destructive" : "border-border"}`}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
              </div>

              {/* Program Interest */}
              <div className="flex flex-col gap-2">
                <label htmlFor="program" className="text-sm font-bold tracking-wide">
                  Area of Interest <span className="text-destructive">*</span>
                </label>
                <NativeSelect
                  id="program"
                  name="program"
                  value={form.program}
                  onChange={handleChange}
                  invalid={!!errors.program}
                >
                  <option value="">Select a program or topic…</option>
                  {programs.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </NativeSelect>
                {errors.program && <p className="text-xs text-destructive">{errors.program}</p>}
              </div>

              {/* Message */}
              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-sm font-bold tracking-wide">
                  Message <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  maxLength={2000}
                  rows={6}
                  placeholder="Tell us about yourself, your situation, or how you'd like to get involved…"
                  className={`w-full rounded-xl border px-4 py-3 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition resize-none ${errors.message ? "border-destructive focus:ring-destructive" : "border-border"}`}
                />
                <div className="flex justify-between items-center">
                  {errors.message ? <p className="text-xs text-destructive">{errors.message}</p> : <span />}
                  <p className="text-xs text-muted-foreground ml-auto">{form.message.length}/2000</p>
                </div>
              </div>

              <div className="flex flex-col items-center md:items-start gap-2">
                <div ref={turnstileRef} className="cf-turnstile" />
              </div>

              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}
              <button
                type="submit"
                disabled={submitting || !turnstileToken}
                className="self-center md:self-start inline-flex items-center gap-2 px-10 py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Alt CTA */}
      <section className="bg-secondary py-16 md:py-20 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase text-muted-foreground mb-4">Ready to Give?</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Make a Difference Today</h2>
          <p className="text-muted-foreground text-base max-w-lg mx-auto mb-8">
            Your donation — no matter the size — brings us closer to opening our first Community Housing Center.
          </p>
          <a
            href={GIVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity"
          >
            Give Now
          </a>
        </div>
      </section>
    </main>
  );
}